import anime from 'animejs';
import { $, html, on, rune, View } from 'rune-ts';
import { BannerTypeS } from '../../../../../features/Banner/outbound/share';
import { ButtonAction } from '../../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { staticTypoPcMo, typo, typoPcMo } from '../../../../../shared/typography/typo';
import { replaceNewlinesWithBr } from '../../../../../shared/util';
import { dataStr } from '../../../../../shared/util/dataStr';
import { makeDefaultProfileImg } from '../../../../../shared/util/helper';
import { getResizedUrl } from '../../../../../shared/util/image';
import { ButtonArrow } from '../../../../components/atoms/ButtonArrow/ButtonArrow';
import { arrowIcon } from '../../../../components/atoms/Icon';
import { SwiperChangeEvent, SwiperView } from '../../../../components/cells/Swiper/Swiper';
import bannerClass from './MainBanner.module.scss';

interface BannerPaginationData {
  theme?: string | null;
  current_index: number;
  max_index: number;
}

interface BannerNavigationData {
  theme?: string | null;
  prev_enable: boolean;
  next_enable: boolean;
}

const default_img = makeDefaultProfileImg(1200);
class MainBannerInformationView extends View<{ information: BannerTypeS.MainBannerData; active: boolean }> {
  deactivateAnime?: anime.AnimeInstance;
  activateAnime?: anime.AnimeInstance;

  override template() {
    const { navigate, title, sub_title, category, href, target, label_intl, banner_theme } =
      this.data.information;

    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    const label = label_intl?.[ET.lang];

    return html`
      <div
        data-theme="${banner_theme ?? ''}"
        class="${this.data.active ? bannerClass.active : bannerClass.deactivate} ${bannerClass.information}"
      >
        <${href ? 'a' : 'div'}
          ${navigate ? html`data-post-message="${dataStr(navigate)}"` : ''}
          href="${html.preventEscape(href ?? '#')}"
          target="${target}"
          class="${bannerClass.contents}">
          <span class="class="${bannerClass.category}" ${staticTypoPcMo({
            is_mobile,
            mo: 'unica_12_medium',
            pc: 'unica_16_medium',
          })}"
            >${html.preventEscape(category ?? '')}</span
          >
          <h3 class="${bannerClass.title} ${typoPcMo({ is_mobile, pc: '48_bold', mo: '24_bold' })}">
            ${html.preventEscape(replaceNewlinesWithBr(title))}
          </h3>
          ${
            is_mobile
              ? ''
              : html`<span class="${typo('16_medium')}"
                  >${html.preventEscape(replaceNewlinesWithBr(sub_title))}</span
                >`
          }
        </${href ? 'a' : 'div'}>
        ${
          is_mobile
            ? ''
            : href && label
              ? html`<a
                  class="${bannerClass.button}"
                  target="${target}"
                  href="${html.preventEscape(href ?? '#')}"
                >
                  ${new ButtonAction({
                    label: html`${html.preventEscape(label)}
                      <span class="${bannerClass.arrow}">${arrowIcon}</span>`,
                    type: banner_theme === 'black' ? 'blue' : 'white',
                  })}
                </a>`
              : ''
        }
      </div> 
    `;
  }

  activate() {
    if (this.deactivateAnime && !this.deactivateAnime?.paused) {
      this.deactivateAnime?.pause();
    }

    const el = this.element();
    $(el).removeClass(bannerClass.deactivate);

    if (this.activateAnime) {
      this.activateAnime.restart();
      return;
    }

    this.activateAnime = anime({
      targets: el,
      opacity: 1,
      duration: 1000,
      easing: 'easeInOutQuad',
      complete: () => {
        $(el).addClass(bannerClass.active);
        el.style.opacity = '';
      },
    });
  }

  deactivate() {
    if (this.activateAnime && !this.activateAnime?.paused) {
      this.activateAnime?.pause();
    }

    if (this.deactivateAnime) {
      this.deactivateAnime.restart();
      return;
    }

    const el = this.element();

    this.deactivateAnime = anime({
      targets: el,
      opacity: 0,
      duration: 1000,
      easing: 'easeInOutQuad',
      complete: () => {
        $(el).addClass(bannerClass.deactivate).removeClass(bannerClass.active);
      },
    });
  }
}

class MainBannerImageView extends View<BannerTypeS.MainBannerData> {
  protected override template() {
    const { url, target, href, navigate } = this.data;

    return html`
      <${href ? 'a' : 'div'}
        class="${bannerClass.image_wrapper}"
        ${navigate ? html`data-post-message="${dataStr(navigate)}"` : ''}
        ${href ? html`href = "${html.preventEscape(href)}"` : ''}
        ${href ? html`target="${target}"` : ''}
      >
        <img class="${bannerClass.image}" src="${
          url ? getResizedUrl({ url, width: 2560, quality: 90, format: 'webp' }) : default_img
        }" alt="" />
      </${href ? 'a' : 'div'}>
    `;
  }
}

class MainBannerNavigationView extends View<BannerNavigationData> {
  override template() {
    const navigation = this.data;
    return html` <div class="navigation ${bannerClass.navigation}">
      ${navigation.prev_enable
        ? new ButtonArrow({ direction: 'left', type: navigation.theme === 'black' ? 'dim' : 'bright' })
        : ''}
      ${navigation.next_enable
        ? new ButtonArrow({ direction: 'right', type: navigation.theme === 'black' ? 'dim' : 'bright' })
        : ''}
    </div>`;
  }

  protected override onRender() {
    this.delegate('click', ButtonArrow, (_e, targetView) => {
      this.dispatchEvent(NavigateHandleEvent, {
        bubbles: true,
        detail: {
          direction: targetView.data.direction === 'left' ? 'prev' : 'next',
        },
      });
    });
  }

  changeTheme(theme: string) {
    if (rune.getSharedData(this)?.is_mobile) return;
    if (this.data.theme === theme) return;

    this.data.theme = theme;
    this.subViews(ButtonArrow).forEach((button) => {
      button.changeType(theme === 'black' ? 'dim' : 'bright');
    });
  }
}

class NavigateHandleEvent extends CustomEvent<{
  direction: 'prev' | 'next';
}> {}

class MainBannerPaginationView extends View<BannerPaginationData> {
  protected override template() {
    const pagination = this.data;
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html`<div
      data-theme="${pagination.theme ?? ''}"
      class="pagination ${bannerClass.pagination} ${staticTypoPcMo({
        is_mobile,
        mo: 'bebas_14_bold',
        pc: 'bebas_16_bold',
      })}"
    >
      <span class="current_index"> ${this.calcIndex(pagination.current_index)} </span>
      <div class="${bannerClass.pages}">
        <div class="progress ${bannerClass.page}" style="width: ${this.calcProgress()}%;"></div>
      </div>
      <span> ${this.calcIndex(pagination.max_index)} </span>
    </div>`;
  }

  calcIndex(index: number) {
    return `${index + 1 < 10 ? '0' : ''}${index + 1}`;
  }

  calcProgress() {
    const pagination = this.data;

    return ((pagination.current_index + 1) / (pagination.max_index + 1)) * 100;
  }

  setCurrentIndex(index: number) {
    this.data.current_index = index;
  }

  override redraw(): this {
    const pagination = this.data;
    $(this.element()).find('.current_index')?.setInnerHtml(this.calcIndex(pagination.current_index));
    const progress = $(this.element()).find('.progress')?.element();
    if (progress) {
      progress.style.width = `${this.calcProgress()}%`;
    }
    this.element().dataset.theme = pagination.theme ?? '';
    return this;
  }
}

export class MainBannerView extends View<BannerTypeS.MainBannerData[]> {
  informationViews: MainBannerInformationView[];
  paginationView: MainBannerPaginationView;
  navigationView: MainBannerNavigationView;
  swiperView: SwiperView<BannerTypeS.MainBannerData, typeof MainBannerImageView>;

  constructor(data: BannerTypeS.MainBannerData[]) {
    super(data);

    const current_index = 0;

    this.informationViews = data.map(
      (banner, index) =>
        new MainBannerInformationView({ information: banner, active: index === current_index }),
    );
    this.paginationView = new MainBannerPaginationView({
      current_index,
      max_index: data.length - 1,
      theme: data[current_index]?.banner_theme,
    });
    this.navigationView = new MainBannerNavigationView({
      theme: data[current_index]?.banner_theme,
      prev_enable: data.length > 1,
      next_enable: data.length > 1,
    });
    this.swiperView = new SwiperView(
      {
        loop: true,
        speed: 800,
        autoplay:
          data.length > 1
            ? {
                delay: 6000,
                pauseOnMouseEnter: true,
                waitForTransition: true,
              }
            : false,
      },
      {
        swiper: bannerClass.swiper,
        wrapper: bannerClass.wrapper,
      },
      MainBannerImageView,
      [...data],
    );
  }

  protected override template() {
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    return html` <div class="${bannerClass.banner}">
      ${this.informationViews}
      <div class="${bannerClass.controller}">
        ${this.data.length > 1 ? this.paginationView : ''} ${is_mobile ? '' : this.navigationView}
      </div>
      ${this.swiperView}
    </div>`;
  }

  @on(SwiperChangeEvent)
  swiperHandler(e: SwiperChangeEvent) {
    const { current_index, previous_index } = e.detail;
    if (!rune.getSharedData(this)?.is_mobile) {
      this.navigationView.changeTheme(this.data[current_index]?.banner_theme ?? '');
    }

    this.paginationView.data.theme = this.data[current_index]?.banner_theme ?? '';
    this.paginationView.setCurrentIndex(current_index);
    this.paginationView.redraw();
    this.informationViews[previous_index]?.deactivate();
    this.informationViews[current_index]?.activate();
  }

  @on(NavigateHandleEvent)
  navigationHandler(e: NavigateHandleEvent) {
    this.swiperView[e.detail.direction]();
  }
}
